import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import type { LogoDto, OrganizationDto, OrganizationInput } from "@delta/shared";
import { useLang } from "../../i18n";
import { useSession } from "../../lib/session";
import { api, apiUpload } from "../../lib/api";
import { errorText, fieldErrorsOf } from "../../lib/errors";
import { Icon } from "../../components/Icon";
import { PageHead, Shell } from "../../components/Shell";
import { useToast } from "../../components/Toast";
import { AccountSelect, useAccountOptions } from "../accounting/common";
import { DemoLockNotice } from "../../components/Demo";

// Settings › Organization, ported from the prototype (erp-settings.js "/settings/organization"): a page form with
// four sections and an action bar that tracks unsaved changes. The logo is chosen here and uploaded on Save. The
// default customer and supplier accounts (rule C-1, provisional; decision D-18) are what a new client or supplier
// takes as its account when none is chosen. The opening stock and stock difference accounts (rule S-5, provisional)
// are what beginning inventory is credited to and what count differences are posted to. The legal invoicing system
// is named on every non-fiscal POS receipt while ZATCA phase 2 is deferred (docs/non-fiscal-pos.md).

const LOGO_TYPES = ["image/png", "image/jpeg", "image/webp"];
const LOGO_MAX = 512 * 1024;
const ACCOUNTS = ["customerAccountId", "supplierAccountId", "openingStockAccountId", "stockDifferenceAccountId"] as const;
type AccountField = (typeof ACCOUNTS)[number];
type TextField = Exclude<keyof OrganizationInput, AccountField>;
type Form = Record<TextField | AccountField, string>;
const FIELDS: TextField[] = ["nameAr", "nameEn", "taxNumber", "commercialRecord", "note", "phone", "landline", "fax", "email", "country", "city", "neighborhood", "street", "buildingNumber", "postalCode", "legalInvoiceSystem"];
const ALL = [...FIELDS, ...ACCOUNTS] as const;
const ACCOUNT_LABEL: Record<AccountField, string> = { customerAccountId: "customerAccount", supplierAccountId: "supplierAccount", openingStockAccountId: "openingStockAccount", stockDifferenceAccountId: "stockDifferenceAccount" };
const pick = (o: OrganizationDto): Form => Object.fromEntries(ALL.map((k) => [k, o[k] ?? ""])) as Form;

export function OrganizationSettings() {
  const { t, L } = useLang();
  const s = useSession();
  const toast = useToast();
  const [org, setOrg] = useState<OrganizationDto | null>(null);
  const [v, setV] = useState<Form | null>(null);
  const accounts = useAccountOptions();
  const [logo, setLogo] = useState<{ file: File | null; url: string | null } | null>(null); // null: unchanged
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [logoV, setLogoV] = useState(0);

  const load = useCallback(async () => {
    setFailed(null);
    try {
      const o = await api<OrganizationDto>("GET", "/settings/organization");
      setOrg(o); setV(pick(o)); setLogo(null); setErrs({});
    } catch (e) { setFailed(errorText(t, e)); }
  }, [t]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => () => { if (logo?.url) URL.revokeObjectURL(logo.url); }, [logo]);

  const dirty = useMemo(() => !!org && !!v && (logo !== null || ALL.some((k) => v[k] !== (org[k] ?? ""))), [org, v, logo]);
  const set = (k: keyof Form, x: string) => { setV((o) => ({ ...o!, [k]: x })); setErrs((e) => ({ ...e, [k]: "" })); };

  function chooseLogo(file: File | undefined) {
    if (!file) return;
    if (!LOGO_TYPES.includes(file.type)) { toast(t("logoType")); return; }
    if (file.size > LOGO_MAX) { toast(t("logoTooLarge")); return; }
    setLogo({ file, url: URL.createObjectURL(file) });
  }

  async function save() {
    if (!v) return;
    const e: Record<string, string> = {};
    if (!v.nameAr.trim()) e.nameAr = t("requiredField");
    if (!v.nameEn.trim()) e.nameEn = t("requiredField");
    if (v.taxNumber.trim() && !/^3\d{13}3$/.test(v.taxNumber.trim())) e.taxNumber = t("vatFormat");
    if (v.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.email.trim())) e.email = t("emailFormat");
    setErrs(e);
    if (Object.keys(e).length) { document.querySelector<HTMLInputElement>(`#pf-${Object.keys(e)[0]}`)?.focus(); return; }
    setBusy(true);
    try {
      if (logo?.file) await apiUpload<LogoDto>("/settings/organization/logo", logo.file);
      else if (logo && !logo.file && org?.logo) await api("DELETE", "/settings/organization/logo");
      const body: OrganizationInput = { ...v, ...Object.fromEntries(ACCOUNTS.map((k) => [k, v[k] || null])) };
      const o = await api<OrganizationDto>("PUT", "/settings/organization", body);
      setOrg(o); setV(pick(o)); setLogo(null); setLogoV((n) => n + 1);
      toast(t("orgSaved"));
      void s.refresh();
    } catch (err) {
      setErrs(fieldErrorsOf(t, err));
      toast(errorText(t, err));
    } finally { setBusy(false); }
  }

  const fld = (k: TextField, label: string, o: { req?: boolean; ltr?: boolean; span?: string; hint?: string; type?: string } = {}) => (
    <div className={`field ${o.span ? "span-" + o.span : ""} ${errs[k] ? "invalid" : ""}`}>
      <label htmlFor={`pf-${k}`} className={o.req ? "req" : ""}>{label}</label>
      <input id={`pf-${k}`} className={`control ${o.ltr ? "num" : ""}`} style={{ textAlign: "start" }} type={o.type ?? "text"} dir={o.ltr ? "ltr" : undefined}
        value={v![k]} onChange={(e) => set(k, e.target.value)} autoComplete="off" />
      <div className="ferr" role="alert">{errs[k]}</div>{o.hint && <span className="hint">{o.hint}</span>}
    </div>
  );
  const sec = (title: string, desc: string | null, body: ReactNode) => (
    <section className="set-sec"><div className="set-sec-h"><h2>{title}</h2>{desc && <p>{desc}</p>}</div><div className="card set-sec-b"><div className="form-grid">{body}</div></div></section>
  );

  const shownLogo = logo ? logo.url : org?.logo ? `/api/organization/logo?v=${encodeURIComponent(org.logo.updatedAt)}-${logoV}` : null;
  const body = failed ? (
    <section className="card"><div className="state-box error"><Icon name="alert" /><h3>{t("loadErrorTitle")}</h3><p>{failed}</p><button className="btn btn-primary" onClick={() => void load()}><Icon name="refresh" />{t("retry")}</button></div></section>
  ) : !v ? <section className="card"><div className="state-box"><p>{t("loading")}</p></div></section> : (
    <div className="set-stack">
      {sec(t("orgData"), t("orgDataHint"), <>
        <div className="field full"><span className="label">{t("logo")}</span>
          <div className="logo-row">
            <div className="logo-box">{shownLogo ? <img src={shownLogo} alt={t("logo")} data-testid="org-logo" /> : <span>{(L({ nameEn: v.nameEn, nameAr: v.nameAr }) || "?").slice(0, 1)}</span>}</div>
            <label className="dropzone sm"><Icon name="upload" /><b>{t("chooseImage")}</b><span className="hint">{t("logoHint")}</span>
              <input type="file" accept={LOGO_TYPES.join(",")} className="sr-only" data-testid="logo-input" onChange={(e) => { chooseLogo(e.target.files?.[0]); e.target.value = ""; }} /></label>
            {shownLogo && <button type="button" className="btn btn-sm btn-danger-ghost" onClick={() => setLogo({ file: null, url: null })}><Icon name="trash" />{t("removeLogo")}</button>}
          </div>
        </div>
        {fld("nameAr", t("nameAr"), { req: true })}{fld("nameEn", t("nameEn"), { req: true })}
        {fld("taxNumber", t("taxNumber"), { ltr: true, hint: t("vatHint") })}{fld("commercialRecord", t("commercialRecord"), { ltr: true })}
        {fld("note", t("note"), { span: "2" })}
      </>)}
      {sec(t("contactInfo"), null, <>{fld("phone", t("phone"), { ltr: true, type: "tel" })}{fld("landline", t("landline"), { ltr: true, type: "tel" })}{fld("fax", t("fax"), { ltr: true, type: "tel" })}{fld("email", t("emailF"), { ltr: true, span: "2", type: "email" })}</>)}
      {sec(t("addressF"), null, <>{fld("country", t("country"))}{fld("city", t("city"))}{fld("neighborhood", t("region"))}{fld("street", t("street"), { span: "full" })}
        {fld("buildingNumber", t("buildingNumber"), { ltr: true, hint: t("addressZatcaHint") })}{fld("postalCode", t("postalCode"), { ltr: true })}</>)}
      {sec(t("legalInvoicingSec"), t("legalInvoicingHint"), <>
        {fld("legalInvoiceSystem", t("legalInvoiceSystemF"), { span: "2" })}
        <div className="dep-note full" data-testid="nonfiscal-note"><Icon name="info" /><span>{t("legalInvoicingNote")}</span></div>
      </>)}
      {sec(t("controlAccounts"), null, <>
        {ACCOUNTS.map((k) => (
          <div key={k} className={`field ${errs[k] ? "invalid" : ""}`}>
            <label htmlFor={`pf-${k}`}>{t(ACCOUNT_LABEL[k])}</label>
            <AccountSelect id={`pf-${k}`} value={v[k]} onChange={(x) => set(k, x)} opts={accounts.opts} failed={accounts.failed} empty="–" />
            <div className="ferr" role="alert">{errs[k]}</div>
          </div>
        ))}
        <div className="dep-note full" data-testid="default-accounts-note"><Icon name="info" /><span>{t("defaultAccountsProvisional")} {t("stockAccountsProvisional")}</span></div>
      </>)}
    </div>
  );

  const bar = v && (
    <div className="action-bar">
      <div className="ab-stats"><div><div className="k">{t("status")}</div><div className="v" data-testid="form-state">{dirty ? <span className="pill warn">{t("unsavedChanges")}</span> : <span className="pill good">{t("allSaved")}</span>}</div></div></div>
      <div className="ab-actions">
        <button className="btn" disabled={!dirty || busy} onClick={() => { setV(pick(org!)); setLogo(null); setErrs({}); }}>{t("discardChanges")}</button>
        <button className="btn btn-primary" disabled={!dirty || busy} onClick={() => void save()}><Icon name="check" />{busy ? t("saving") : t("save")}</button>
      </div>
    </div>
  );

  return (
    <Shell navKey="/settings/organization" bar={bar}>
      <DemoLockNotice />
      <PageHead crumbs={[[t("home"), "/home"], [t("settings")], [t("setOrg")]]} title={t("settingsOrg")} />
      {body}
    </Shell>
  );
}
